<template>
	<Teleport to="body">
		<DragableContainer v-if="showDialog" :position="position">
			<Drawing2DImageFloatingPoint :source="source.position" :destination="destination.position" @onSubmit="onSubmit"
				@onCancel="onCancel">
			</Drawing2DImageFloatingPoint>
		</DragableContainer>
	</Teleport>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import Drawing2DImageFloatingPoint from "./Drawing2DImageFloatingPoint.vue";
import { mapActions } from "vuex/dist/vuex.cjs.js";
import DragableContainer from "./DragableContainer.vue";
import { convertCoordinatesToCesiumCrs } from "../../utils/cesium-common";
import { notifySuccess } from "@/utils/notificationUtils";

export default {
	components: {
		Drawing2DImageFloatingPoint,
		DragableContainer,
	},
	computed: {
		...mapState("drawing2dImage", [
			"pointsOfTwoPointAlignment",
			"imageUnder2PointAlignment",
		]),
	},
	watch: {
		showDialog(newVal) {
			// When showDialog is set to false (e.g., by cancel2pointsAlignment in Vuex),
			// resolve any pending Promise to allow mounted() loop to continue
			if (!newVal && this.destinationResolve) {
				this.destinationResolve();
				this.destinationResolve = null;
			}
		},
		pointsOfTwoPointAlignment() {
			if (this.lockedIndex === null) {
				const count = this.pointsOfTwoPointAlignment.filter(
					(item) => !!item,
				).length;
				this.lockedIndex = count === 2 ? 1 : null;
			}
		},
	},
	data() {
		return {
			position: { x: 0, y: 0 },
			source: null,
			destination: null,
			showDialog: false,
			destinationResolve: null,
		};
	},
	methods: {
		...mapMutations(["set_snackbar", "reset_snackbars"]),
		...mapActions("drawing2dImage", [
			"deletePointForTwoPointsAlignmentByIndex",
			"addPointForTwoPointsAlignment",
		]),
		toViewportPosition(mousePosition) {
			const cesiumElement = this.$parent.$el;
			const rect = cesiumElement.getBoundingClientRect();
			return {
				x: mousePosition.x + rect.left,
				y: mousePosition.y + rect.top,
			};
		},
		onClickAway(e) {
			// e.stopImmediatePropagation();
			console.log("Click away");
			this.isFocussed = false;
		},
		async onSubmit() {
			this.$store.getters.viewer.endPickingDestinationPointFor2PointsAlignment();
			this.destinationResolve?.(this.destination);
			this.addPointForTwoPointsAlignment({
				pointInfo: {
					index: this.pointsOfTwoPointAlignment.length,
					source: { x: this.source.position.x, y: this.source.position.y },
					destination: {
						x: this.destination.position.x,
						y: this.destination.position.y,
					},
				},
			});
			this.source = null;
			this.destination = null;
			this.showDialog = !!(this.source && this.destination);
			if (this.pointsOfTwoPointAlignment.length === 2) {
				await this.$store.dispatch("executeWithSpinner", async () => {
					await this.$store.dispatch("drawing2dImage/end2pointsAlignment", {
						twoPointArgs: {
							source1: {
								x: this.pointsOfTwoPointAlignment[0].source.x,
								y: this.pointsOfTwoPointAlignment[0].source.y,
							},
							source2: {
								x: this.pointsOfTwoPointAlignment[1].source.x,
								y: this.pointsOfTwoPointAlignment[1].source.y,
							},
							destination1: {
								x: this.pointsOfTwoPointAlignment[0].destination.x,
								y: this.pointsOfTwoPointAlignment[0].destination.y,
							},
							destination2: {
								x: this.pointsOfTwoPointAlignment[1].destination.x,
								y: this.pointsOfTwoPointAlignment[1].destination.y,
							},
						},
					});
				});
			}
		},
		async onCancel() {
			await this.$store.dispatch("drawing2dImage/cancel2pointsAlignment");
			this.source = null;
			this.destination = null;
			this.showDialog = false;
		},
	},
	async mounted() {
		const noOfPoints = 2;
		const json = this.$store.getters.viewer.getJSONOfDrawing2dImage(
			this.imageUnder2PointAlignment.getId(),
		);
		const corners = json.localCornerPositions;
		if (json && corners && corners.length === 4) {
			const wgs84Corners = await convertCoordinatesToCesiumCrs(corners);
			this.$store.getters.viewer.set2DView(
				wgs84Corners[0],
				wgs84Corners[1],
				wgs84Corners[2],
				wgs84Corners[3],
			);
			for (let i = 0; i < noOfPoints; i++) {
				// Show instruction message until user completes the operation (timeout: -1 for manual dismissal)
				notifySuccess(
					{ $store: this.$store, $t: this.$t },
					this.$t("CLICK_TO_SELECT_SOURCE_POINT", { number: i + 1 }),
					-1,
				);
				this.$store.getters.viewer.updateDrawing2dImageVisibility(
					this.imageUnder2PointAlignment.getId(),
					true,
				);
				this.source =
					await this.$store.getters.viewer.pickSourcePointFor2PointsAlignment(
						"red",
					);
				this.reset_snackbars();
				// Show instruction message until user completes the operation (timeout: -1 for manual dismissal)
				notifySuccess(
					{ $store: this.$store, $t: this.$t },
					this.$t("CLICK_TO_SELECT_DESTINATION_POINT", { number: i + 1 }),
					-1,
				);
				this.$store.getters.viewer.updateDrawing2dImageVisibility(
					this.imageUnder2PointAlignment.getId(),
					false,
				);

				await new Promise(async (resolve, reject) => {
					this.destinationResolve = resolve;
					this.$store.getters.viewer.startPickingDestinationPointFor2PointsAlignment(
						"blue",
						({ position, mousePosition }) => {
							const viewportPosition = this.toViewportPosition(mousePosition);
							this.position.x = viewportPosition.x;
							this.position.y = viewportPosition.y;
							this.destination = { position, mousePosition };
							this.showDialog = !!(this.source && this.destination);
						},
					);
				});
				this.reset_snackbars();
			}
		}
	},
};
</script>

<style lang="scss" scoped>
.fullscreen {
	position: absolute;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vw;
	background-color: transparent;
	z-index: 9999;
}

.topoverlap {
	position: absolute;
	background-color: rgba(0.5, 0.5, 0.5, 0.5);
	height: 2rem;
	;
	width: 100%;
	top: 0;
	z-index: 9999;
	display: flex;
	justify-content: center;
}

.btn {
	background-color: var(--primary-bg);
}
</style>
